// Header HUD — count, rate, phase pill, effect pills, pump emoji.
// Mostly preact subscribers. renderStreak() stays imperative because it
// reads streakCount which lives in main.js (the tap handler's local var).
import { render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { state } from '../systems/state.js';
import { rate } from '../systems/rate.js';
import { fmt } from '../systems/util.js';
import { currentPhase, PHASE_DURATION_MS } from '../systems/phases.js';
import { useStore } from './use-store.js';
import { subscribe } from '../systems/store.js';

function Count() {
  // Smooth count — extrapolate forward from state.count using current rate
  // and time since last tick. state.count itself only changes 1× per second
  // (or on tap), but the display grows continuously between ticks.
  const ref = useRef(null);
  const [, force] = useState(0);
  useEffect(() => {
    let raf;
    const tick = () => { force(n => n + 1); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  // Clamp the projection to 1.5s in case state.lastTick is stale (backgrounded
  // tab, paused tick). Floor at 0 for the brief window between defaultState()
  // and the first tick.
  const elapsed = Math.max(0, Math.min(1.5, (Date.now() - (state.lastTick || Date.now())) / 1000));
  const projected = state.count + elapsed * rate();
  return <span ref={ref}>{fmt(projected)}</span>;
}

function Rate() {
  useStore();
  const r = rate();
  if (r <= 0) return null;
  return <>+{fmt(r)} / sec</>;
}

function Phase() {
  useStore();
  // Phase pill needs to tick each second even if nothing else changed
  // (the countdown decreases). We force a re-render at 1Hz via local state.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const ph = currentPhase();
  const remaining = Math.max(0, PHASE_DURATION_MS - (Date.now() - state.phaseStartAt));
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000).toString().padStart(2, '0');
  return (
    <span title={ph.desc}>
      <span className="phase-icon">{ph.icon}</span>
      <span className="phase-name">{ph.name}</span>
      <span className="phase-time">{minutes}:{seconds}</span>
    </span>
  );
}

function Effects() {
  useStore();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const now = Date.now();
  const live = (state.activeEffects || []).filter(e => now < e.expiresAt);
  return (
    <>
      {live.map(e => {
        const remaining = Math.ceil((e.expiresAt - now) / 1000);
        const kind = e.mult >= 1 ? 'buff' : 'debuff';
        const sign = e.mult >= 1 ? `×${e.mult}` : `×${e.mult.toFixed(2)}`;
        // Compound key — id alone collides if the same buff fires twice
        // back-to-back (two frenzy procs); expiresAt makes it stable per
        // instance.
        return <span key={`${e.id}-${e.expiresAt}`} className={'effect-pill ' + kind}>{sign} · {remaining}s</span>;
      })}
    </>
  );
}

// Pump emoji stays imperative — preact rendering into #pump would clobber
// its child structure (#pump-inner with the bounce target + #pump-aura
// for the combo flash) which physics.js holds refs to. Mutate the first
// child text node of #pump-inner instead, leaving #pump-aura untouched.
const pumpInnerEl = document.getElementById('pump-inner');
export function setPumpEmoji() {
  if (!pumpInnerEl) return;
  const target = state.owned.crowncake ? '👑' : '🍞';
  // First child node should be the text "🍞" / "👑"; #pump-aura is the
  // second child. Replace just the text without touching the aura span.
  if (pumpInnerEl.firstChild && pumpInnerEl.firstChild.nodeType === 3) {
    pumpInnerEl.firstChild.nodeValue = target;
  }
}

export function mountHeader() {
  render(<Count />, document.getElementById('count'));
  render(<Rate />, document.getElementById('rate'));
  render(<Phase />, document.getElementById('phase'));
  render(<Effects />, document.getElementById('effects'));
  // Pump emoji + companion stay imperative (see setPumpEmoji + syncCompanion
  // notes). Subscribe both so they auto-update on state changes.
  subscribe(setPumpEmoji);
  subscribe(syncCompanionVisibility);
  setPumpEmoji();
  syncCompanionVisibility();
}

// Companion stays imperative — main.js's scheduleWink swaps textContent
// directly, which would race with preact rendering. Just toggle visibility
// when state changes (called from main.js boot + ascend).
const companionEl = document.getElementById('companion');
export function syncCompanionVisibility() {
  if (companionEl) companionEl.classList.toggle('show', !!state.hasEgg);
}

// renderStreak stays imperative — streakCount lives in main.js (the tap
// handler owns it). Keeping the function signature so call sites don't
// change.
const streakEl = document.getElementById('streak');
const pumpEl = document.getElementById('pump');
let lastStreakTier = -1;
export function renderStreak(streakCount, tierIndex, tierMult) {
  if (tierIndex === lastStreakTier && streakCount < 5) return;
  if (streakCount >= 5) {
    streakEl.textContent = tierIndex > 0 ? `🔥 ×${tierMult} (streak ${streakCount})` : `streak ${streakCount}`;
    streakEl.classList.add('show');
  } else {
    streakEl.classList.remove('show');
  }
  pumpEl.classList.toggle('combo-1', tierIndex === 1);
  pumpEl.classList.toggle('combo-2', tierIndex === 2);
  pumpEl.classList.toggle('combo-3', tierIndex === 3);
  lastStreakTier = tierIndex;
}

// resetHeaderCache is the only real cleanup — clears the streak-tier cache
// so renderStreak repaints from a known state after ascend/reset.
export function resetHeaderCache() { lastStreakTier = -1; }
